import React from 'react';
import { cn, cardClassName } from '@jira-clone/ui';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render?: (item: T) => React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: (item: T) => string;
  onRowClick?: (item: T) => void;
  empty?: React.ReactNode;
  className?: string;
  /** Render without the card surface, for a table that already sits inside a Card. */
  bare?: boolean;
}

export function Table<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  empty = 'No data found.',
  className = '',
  bare = false,
}: TableProps<T>) {
  if (data.length === 0) {
    return <div className="text-center py-10 text-muted-foreground text-sm">{empty}</div>;
  }

  return (
    <div className={cn(bare ? 'overflow-x-auto' : cn(cardClassName(), 'overflow-x-auto'), className)}>
      <table className="w-full text-left text-sm border-collapse">
        <thead className="border-b border-border/50 bg-muted/40 text-muted-foreground font-medium">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider ${col.className || ''}`}
                style={{ textAlign: col.align }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {data.map((item) => (
            <tr
              key={rowKey(item)}
              onClick={() => onRowClick?.(item)}
              className={`transition-colors hover:bg-muted/40 ${onRowClick ? 'cursor-pointer' : ''}`}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-4 py-3.5 text-foreground ${col.className || ''}`}
                  style={{ textAlign: col.align }}
                >
                  {col.render ? col.render(item) : (item as any)[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
